import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
} from "reactstrap";

import { withRouter } from "react-router-dom";
import { compose } from "react-apollo";

import { withQuery } from "containers/withQuery";

import Table from "components/Table/Table";
import {
  formatDate,
  timeMask,
  dateMask,
} from "utils/dateFormats";
import {
  formatVolume,
  formatWeight,
  formatPercentual,
} from "utils/formatters";

import {
  Title,
  DataTitle,
  DataCard,
  DataValue,
  CanceledBanner,
} from "./ReportComponents";

import CONCRETE_SHIPMENT_REPORT_QUERY from "./graphql/concreteShipmentReportQuery";

const humidityHeaders = [
  {
    label: "Material",
    key: "name",
  },
  {
    label: "Umidade",
    key: "value",
    valueFormatter: formatPercentual,
  },
];

const locationsHeaders = [
  {
    label: "Local de estoque",
    key: "name",
  },
  {
    label: "Material no traço",
    key: "formulaQuantity",
    valueFormatter: formatWeight,
  },
  {
    label: "Material consumido",
    key: "consumedQuantity",
    valueFormatter: formatWeight,
  },
  {
    label: "Erro bruto",
    key: "grossError",
    valueFormatter: formatWeight,
  },
  {
    label: "Erro percentual",
    key: "percentageError",
    valueFormatter: formatPercentual,
  },
];

const ShipmentReportResult = ({
  concreteShipmentByCode: concreteShipment
}) => {
  const [shipmentLocations, setShipmentLocations] = useState([]);
  const [materialsHumidity, setMaterialsHumidity] = useState([]);

  const formatShipmentLocations = (materials, changes) => {
    const locations = materials.flatMap(({ shipmentMaterialLocations }) => shipmentMaterialLocations);
    const formattedShipmentLocations = locations.map(({ inventoryLocation, formulaWeight }) => {
      const inventoryChange = changes.find(({
        inventoryLocation: {
          id: locationId
        },
        manualOperation
      }) => locationId === inventoryLocation.id && !manualOperation);
      const consumedQuantity = inventoryChange ? -inventoryChange.quantity : 0;
      const grossError = consumedQuantity - formulaWeight;
      const percentageError = grossError / formulaWeight;
      return {
        name: inventoryLocation.name,
        formulaQuantity: formulaWeight,
        consumedQuantity,
        grossError,
        percentageError,
      };
    });
    setShipmentLocations(formattedShipmentLocations);
  };

  const formatHumidityMaterials = (materials) => {
    const formattedMaterialsHumidity = materials.reduce((humidityList, { inventoryItem: { name }, humidityMeasurement }) => {
      if (humidityMeasurement) {
        const { value } = humidityMeasurement;
        humidityList.push({ name, value });
      }
      return humidityList;
    }, []);
    setMaterialsHumidity(formattedMaterialsHumidity);
  };

  useEffect(() => {
    if (concreteShipment && concreteShipment.shipmentMaterials) {
      formatHumidityMaterials(concreteShipment.shipmentMaterials);
    } else {
      setMaterialsHumidity([]);
    };
  }, [concreteShipment]);

  useEffect(() => {
    if (concreteShipment && concreteShipment.shipmentMaterials && concreteShipment.inventoryChanges) {
      formatShipmentLocations(concreteShipment.shipmentMaterials, concreteShipment.inventoryChanges);
    } else {
      setShipmentLocations([]);
    };
  }, [concreteShipment]);

  const {
    canceledAt,
    startTime,
    loadingStartTime,
    travelStartTime,
    waterCut,
    quantity,
    shipmentInfo,
    concreteFormula,
  } = concreteShipment || {};

  const {
    concreteFamily,
  } = concreteFormula || {};

  const {
    slump,
  } = concreteFamily || {};

  const {
    description,
  } = slump || {};

  const disableOverflow = true;

  return (
    <React.Fragment>
      {!concreteShipment && (
        <div className="message-container">
          <h3 className="mb-3">Carga não encontrada</h3>
          <p>Não existem cargas com o código informado, verifique se o código informado está correto</p>
        </div>
      )}
      {concreteShipment && (
        <div style={{ backgroundColor: "#f6f8ff", paddingTop: 10 }}>
          {
            canceledAt && (
              <CanceledBanner>
                {`Carga cancelada em ${formatDate(canceledAt, dateMask)} às ${formatDate(canceledAt, timeMask)}`}
              </CanceledBanner>
            )
          }
          {
            shipmentInfo && (
              <React.Fragment>
                <Row>
                  <Col md={4}>
                    <DataCard>
                      <DataTitle>Código do contrato</DataTitle>
                      <DataValue>{shipmentInfo.contractCode}</DataValue>
                    </DataCard>
                  </Col>

                  <Col md={8}>
                    <DataCard>
                      <DataTitle>Nome da obra</DataTitle>
                      <DataValue>{shipmentInfo.constructionName}</DataValue>
                    </DataCard>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <DataCard>
                      <DataTitle>Código do equipamento</DataTitle>
                      <DataValue>{shipmentInfo.equipmentCode}</DataValue>
                    </DataCard>
                  </Col>

                  <Col>
                    <DataCard>
                      <DataTitle>Placa do equipamento</DataTitle>
                      <DataValue>{shipmentInfo.equipmentPlate}</DataValue>
                    </DataCard>
                  </Col>

                  <Col>
                    <DataCard>
                      <DataTitle>Nome do operador</DataTitle>
                      <DataValue>{shipmentInfo.operatorName}</DataValue>
                    </DataCard>
                  </Col>
                </Row>
              </React.Fragment>
            )
          }

          {
            concreteFormula && (
              <Row>
                <Col>
                  <DataCard>
                    <DataTitle>Código do traço</DataTitle>
                    <DataValue>{concreteFormula.code}</DataValue>
                  </DataCard>
                </Col>
        
                <Col>
                  <DataCard>
                    <DataTitle>FCK</DataTitle>
                    <DataValue>{concreteFormula.fck}</DataValue>
                  </DataCard>
                </Col>
        
                <Col>
                  <DataCard>
                    <DataTitle>Slump</DataTitle>
                    <DataValue>{description}</DataValue>
                  </DataCard>
                </Col>
        
                <Col>
                  <DataCard>
                    <DataTitle>Tipo de brita</DataTitle>
                    <DataValue>{concreteFormula.concreteFamily.gravelType}</DataValue>
                  </DataCard>
                </Col>
              </Row>
            )
          }


          <Row>
            <Col>
              <DataCard>
                <DataTitle>Data da carga</DataTitle>
                <DataValue>{formatDate(startTime, dateMask)}</DataValue>
              </DataCard>
            </Col>

            <Col>
              <DataCard>
                <DataTitle>Início da carga</DataTitle>
                <DataValue>{formatDate(startTime, timeMask)}</DataValue>
              </DataCard>
            </Col>

            <Col>
              <DataCard>
                <DataTitle>Equipamento pronto</DataTitle>
                <DataValue>{formatDate(loadingStartTime, timeMask)}</DataValue>
              </DataCard>
            </Col>

            <Col>
              <DataCard>
                <DataTitle>Liberação da carga</DataTitle>
                <DataValue>{formatDate(travelStartTime, timeMask)}</DataValue>
              </DataCard>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card className="p-0">
                <Title>Consumo de material</Title>

                <Table
                  matchHeaders
                  center={false}
                  headers={locationsHeaders}
                  items={shipmentLocations}
                  disableOverflow={false}
                />
              </Card>
            </Col>
          </Row>

          <Row>
            <Col>
              <DataCard>
                <DataTitle>Volume solicitado</DataTitle>
                <DataValue>{formatVolume(quantity)}</DataValue>
              </DataCard>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card>
                <Title>Umidade dos materiais</Title>

                <Table
                  matchHeaders
                  center={false}
                  headers={humidityHeaders}
                  items={materialsHumidity}
                  disableOverflow={disableOverflow}
                />
              </Card>
            </Col>

            <Col>
              <DataCard>
                <DataTitle>Corte de água</DataTitle>
                <DataValue>{formatWeight(waterCut, "-", "litros")}</DataValue>
              </DataCard>
            </Col>
          </Row>
        </div>
      )}
    </React.Fragment>
  );
};

export default compose(
  withQuery(({
    code,
  }) => ({
    noLoader: true,
    query: CONCRETE_SHIPMENT_REPORT_QUERY,
    variables: {
      code,
    },
    fetchPolicy: "network-only",
  })),
  withRouter,
)(ShipmentReportResult);
